<template>
  <div id="credit-return-details" class="fill-height align-start">
    <i-overlay v-if="loading" text="Loading Credit Return details..." />

    <v-row v-else class="g-container-large" no-gutters>
      <v-dialog
        v-model="dialog"
        content-class="i-dialog"
        max-width="400px"
        @click:outside="
          ((dialog = false), (answerStatus = answerUpdateMessage = ''))
        "
        @keydown.esc="
          ((dialog = false), (answerStatus = answerUpdateMessage = ''))
        "
      >
        <v-card>
          <v-card-title class="i-dialog-header-div">
            <span class="g-heading-m g-bold">Confirmation</span>
          </v-card-title>

          <v-card-text class="py-4 text-center">
            <span v-if="hasRejectedItems"
              ><strong>Specify reason for rejection</strong></span
            >
            <span v-else
              ><strong>Specify reason for approval (Optional)</strong></span
            >
            <br />
            <v-text-field
              class="pa-0 mt-2 mb-n1"
              v-model="answerUpdateMessage"
              :label="`Message to ${isILR ? 'Customer' : 'Retailer'}`"
              outlined
              append-icon="mdi-message-reply-text"
              counter="255"
              maxlength="255"
              dense
              required
            ></v-text-field>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="justify-center">
            <v-btn
              rounded
              text
              class="i-btn-text-black"
              @click="((dialog = false), (answerUpdateMessage = ''))"
            >
              <strong>Cancel</strong>
            </v-btn>

            <v-btn
              rounded
              class="i-btn-block-secondary"
              :disabled="hasRejectedItems && !answerUpdateMessage"
              @click="finalise()"
            >
              <strong>Finalise Claim</strong>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-col>
        <i-breadcrumbs :breadcrumbs="computedBreadcrumbs"></i-breadcrumbs>
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <template v-if="$vuetifyDisplay.smAndDown">
          <v-col class="mt-2 pa-0">
            <v-card class="rounded-2 mb-6">
              <v-row no-gutters align="center" class="p-div-title py-3 px-3">
                <v-col cols="12">
                  <p class="g-bold g-heading-m p-title mr-2">
                    Credit Return Reference Number
                    {{ `#${creditReturn.referenceNumber}` }}
                  </p>
                </v-col>
              </v-row>

              <div class="pa-3">
                <p
                  class="g-bold g-border-radius-s text-center pa-2 mt-2"
                  :class="[
                    { 'i-status-block-green': isCreditReturnApproved },
                    { 'i-status-block-red': isCreditReturnRejected },
                    {
                      'i-status-block-orange':
                        !isCreditReturnRejected && !isCreditReturnApproved
                    }
                  ]"
                >
                  CREDIT RETURN STATUS:
                  {{
                    creditReturn.status === 'APPROVED' &&
                    creditReturn.hasRejectedItems
                      ? 'PARTIALLY'
                      : ''
                  }}
                  {{ creditReturn.status }}
                </p>

                <v-divider class="mt-3 mb-2"></v-divider>

                <v-row no-gutters>
                  <v-col cols="6" class="py-1">
                    <span>{{ isILR ? 'CUSTOMER' : 'RETAILER' }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{ creditReturn.store.name }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>CREATED DATE</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{
                      $filters.formatDateTime12h(creditReturn.createdAt)
                    }}</span>
                  </v-col>
                  <template v-if="creditReturn.resolvedAt">
                    <v-col cols="6" class="py-1">
                      <span>RESOLVED DATE</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.formatDateTime12h(creditReturn.resolvedAt)
                      }}</span>
                    </v-col>
                  </template>
                  <v-col cols="6" class="py-1">
                    <span>CREDIT PAYMENT TYPE</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <i-payment-type
                      v-if="creditReturn.creditPaymentType"
                      :paymentType="creditReturn.creditPaymentType"
                      :textOnly="true"
                    />
                  </v-col>
                </v-row>

                <v-divider class="mt-3 mb-2"></v-divider>

                <v-row
                  v-if="creditReturn.reasonMessage"
                  no-gutters
                  class="py-3"
                >
                  <v-col cols="12">
                    <div>
                      <strong
                        >{{ isILR ? 'CUSTOMER' : 'RETAILER' }} CREDIT
                        REASON</strong
                      >
                    </div>
                    <div class="g-border-radius-s i-message">
                      <span>{{ creditReturn.reasonMessage }}</span>
                    </div>
                  </v-col>
                </v-row>

                <v-row
                  v-if="creditReturn.statusUpdateMessage"
                  no-gutters
                  class="py-3"
                >
                  <v-col class="mb-2" cols="12">
                    <div>
                      <strong
                        >MESSAGE TO
                        {{ isILR ? 'CUSTOMER' : 'RETAILER' }}</strong
                      >
                    </div>
                    <div class="g-border-radius-s i-message">
                      <span>{{ creditReturn.statusUpdateMessage }}</span>
                    </div>
                  </v-col>
                </v-row>

                <v-divider class="mt-1 mb-4"></v-divider>

                <div class="g-bold g-border-radius-s i-status-block-gray pa-2">
                  <v-row no-gutters>
                    <v-col cols="9" class="text-right">
                      <span>{{ totalLabel('TOTAL PRODUCT PRICE') }}:</span>
                    </v-col>
                    <v-col cols="3" class="text-left pl-2">
                      <span>{{
                        $filters.currency(
                          supplierCostTotal(creditReturn.totalProduct)
                        )
                      }}</span>
                    </v-col>
                    <template v-if="creditReturn.totalRebate.amountIncGst > 0">
                      <v-col cols="9" class="text-right">
                        <span
                          >REBATE
                          <template v-if="creditReturn.rebatePercentage">
                            /
                            {{
                              $filters.percentage(creditReturn.rebatePercentage)
                            }}
                          </template>
                          <template v-if="!isUsTenant">(INC TAX)</template
                          ><v-icon
                            class="ml-1 mb-1"
                            color="primary"
                            small
                            v-tooltip="{
                              ref: 'creditReturnRebateToolTipContent'
                            }"
                            >mdi-help-circle-outline</v-icon
                          >
                          <span ref="creditReturnRebateToolTipContent">
                            <span
                              >Rebate =
                              {{
                                $filters.percentage(
                                  creditReturn.rebatePercentage
                                )
                              }}</span
                            ><br />
                            <span
                              >Rebate % x Total Product Price (ex Tax)</span
                            > </span
                          >:
                        </span>
                      </v-col>
                      <v-col cols="3" class="text-left pl-2">
                        <span
                          >-{{
                            $filters.currency(
                              supplierCostTotal(creditReturn.totalRebate)
                            )
                          }}</span
                        >
                      </v-col>
                    </template>

                    <template
                      v-if="
                        creditReturn.totalSupplierTradingTerms &&
                        creditReturn.totalSupplierTradingTerms.amountIncGst > 0
                      "
                    >
                      <v-col cols="9" class="text-right">
                        <span class="text-no-wrap"
                          >TRADING TERMS
                          <template
                            v-if="creditReturn.supplierTradingTermsPercentage"
                          >
                            /
                            {{
                              $filters.percentage(
                                creditReturn.supplierTradingTermsPercentage
                              )
                            }}
                          </template>
                          <template v-if="!isUsTenant">(INC TAX)</template
                          ><v-icon
                            class="ml-1 mb-1"
                            color="primary"
                            small
                            v-tooltip="{
                              ref: 'creditReturnSupplierTradingTermsToolTipContent'
                            }"
                            >mdi-help-circle-outline</v-icon
                          >
                          <span
                            ref="creditReturnSupplierTradingTermsToolTipContent"
                          >
                            <span
                              >Trading Terms =
                              {{
                                $filters.percentage(
                                  creditReturn.supplierTradingTermsPercentage
                                )
                              }}</span
                            ><br />
                            <span
                              >Trading Terms % x Total Product Price (ex Tax){{
                                isUsTenant ? '' : ' + Sales Tax'
                              }}</span
                            > </span
                          >:
                        </span>
                      </v-col>
                      <v-col cols="3" class="text-left pl-2">
                        <span
                          >-{{
                            $filters.currency(
                              supplierCostTotal(
                                creditReturn.totalSupplierTradingTerms
                              )
                            )
                          }}</span
                        >
                      </v-col>
                    </template>

                    <template
                      v-if="
                        creditReturn.totalProductTradingTerms &&
                        creditReturn.totalProductTradingTerms.amountIncGst > 0
                      "
                    >
                      <v-col cols="9" class="text-right">
                        <span class="text-no-wrap"
                          >{{ totalLabel('TRADING TERMS') }}:</span
                        >
                      </v-col>
                      <v-col cols="3" class="text-left pl-2">
                        <span
                          >-{{
                            $filters.currency(
                              supplierCostTotal(
                                creditReturn.totalProductTradingTerms
                              )
                            )
                          }}</span
                        >
                      </v-col>
                    </template>
                    <template
                      v-if="
                        creditReturn.totalPlatformFee &&
                        creditReturn.totalPlatformFee.amountIncGst > 0
                      "
                    >
                      <v-col cols="9" class="text-right">
                        <span>{{ totalLabel('PLATFORM FEE') }}:</span>
                      </v-col>
                      <v-col cols="3" class="text-left pl-2">
                        <span
                          >-{{
                            $filters.currency(
                              supplierCostTotal(creditReturn.totalPlatformFee)
                            )
                          }}</span
                        >
                      </v-col>
                    </template>
                  </v-row>

                  <div class="i-total-divider"></div>

                  <v-row no-gutters>
                    <v-col cols="9" class="text-right">
                      <span>{{ totalLabel('CREDIT AMOUNT') }}:</span>
                    </v-col>
                    <v-col cols="3" class="text-left pl-2">
                      <span>{{
                        $filters.currency(
                          supplierCostTotal(creditReturn.totalCredit)
                        )
                      }}</span>
                    </v-col>
                    <v-col
                      cols="9"
                      class="text-right"
                      v-if="creditReturn.creditReturnFee.fee.amountIncGst"
                    >
                      <span
                        >FEE /
                        {{
                          $filters.percentage(
                            creditReturn.creditReturnFee.percentage
                          )
                        }}
                        <template v-if="!isUsTenant">(INC TAX)</template>:</span
                      >
                    </v-col>
                    <v-col cols="3" class="text-left pl-2">
                      <span>{{
                        $filters.currency(
                          supplierCostTotal(creditReturn.creditReturnFee.fee)
                        )
                      }}</span>
                    </v-col>
                  </v-row>

                  <div class="i-total-divider"></div>

                  <v-row no-gutters>
                    <v-col cols="9" class="text-right">
                      <span>{{ totalLabel('TOTAL CREDIT AMOUNT') }}:</span>
                    </v-col>
                    <v-col cols="3" class="text-left pl-2">
                      <span>{{
                        $filters.currency(
                          supplierCostTotal(creditReturn.supplierTotalIncFee)
                        )
                      }}</span>
                    </v-col>
                    <v-col v-if="!isUsTenant" cols="9" class="text-right">
                      <span>TOTAL TAXES:</span>
                    </v-col>
                    <v-col v-if="!isUsTenant" cols="3" class="text-left pl-2">
                      <span>{{
                        $filters.currency(
                          creditReturn.supplierTotalIncFee.totalTax
                        )
                      }}</span>
                    </v-col>
                    <v-col cols="9" class="text-right">
                      <span>TOTAL CREDIT QUANTITY UNITS:</span>
                    </v-col>
                    <v-col cols="3" class="text-left pl-2">
                      <span>{{ creditReturn.totalUnitQuantity }}</span>
                    </v-col>
                  </v-row>
                </div>

                <template v-if="isCreditReturnUpdatable">
                  <v-divider class="mt-3 mb-2"></v-divider>

                  <v-row dense>
                    <v-col cols="6">
                      <v-btn
                        class="mt-1 mb-2 g-full-width"
                        @click="approveAll"
                        color="secondary"
                        :disabled="updating"
                        :loading="updating"
                        outlined
                        rounded
                        small
                      >
                        <v-icon small>mdi-check-circle-outline</v-icon> Approve
                        All
                      </v-btn>
                    </v-col>
                    <v-col cols="6">
                      <v-btn
                        class="mt-1 mb-2 g-full-width i-btn-text-red"
                        @click="rejectAll"
                        :disabled="updating"
                        :loading="updating"
                        rounded
                        small
                        text
                      >
                        <v-icon small>mdi-close-circle-outline</v-icon> Reject
                        All
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-btn
                    class="mt-1 mb-2 g-full-width i-btn-block-secondary"
                    @click="dialog = true"
                    :disabled="updating || hasOutstandingItems"
                    large
                    :loading="updating"
                    rounded
                  >
                    Finalise Claim
                  </v-btn>
                </template>

                <div class="text-center pt-2" v-if="isCreditReturnApproved">
                  <v-btn
                    class="i-btn-block-primary g-full-width"
                    @click="fetchPDF()"
                    :loading="fetchingPDF"
                  >
                    <span>Export Credit Return</span>
                    <v-icon color="white" class="ml-1">mdi-file-pdf-box</v-icon>
                  </v-btn>
                </div>
                <div class="text-center pt-2" v-if="isCreditReturnApproved">
                  <v-btn
                    class="i-btn-block-primary g-full-width"
                    @click="fetchInvoicePDF()"
                    :loading="fetchingInvoicePDF"
                    :disabled="!creditReturn.invoiceNumber"
                  >
                    <span>Export Fees Invoice</span>
                    <v-icon color="white" class="ml-1">mdi-file-pdf-box</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card>

            <v-card
              v-for="item in creditReturn.items"
              :key="item.id"
              class="rounded-2 pa-4 pa-md-6 mb-6"
            >
              <p
                class="g-bold g-border-radius-s py-2 text-center"
                :class="[
                  { 'i-status-block-red': item.status === 'REJECTED' },
                  { 'i-status-block-green': item.status === 'APPROVED' },
                  { 'i-status-block-orange': item.status === 'OUTSTANDING' }
                ]"
              >
                <strong>{{ item.status }}</strong>
              </p>

              <v-row class="mt-2" no-gutters>
                <v-col cols="6" class="py-1">
                  <span>{{ headers[0].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{
                    item.product.categoryProductHierarchy.category
                  }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>{{ headers[1].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{
                    item.product.categoryProductHierarchy.subCategory
                  }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>{{ headers[2].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ item.product.supplierItemId }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>{{ headers[3].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ item.productDescription }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>{{ headers[4].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ item.product.baseUnitGtin }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>UNIT PRICE </span>
                  <br />
                  <span class="p-text-color-red">(EX TAX)</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{
                    $filters.currency(item.unitPriceExTax, '-', 2, 6)
                  }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>{{ headers[6].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ item.quantity }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>PRODUCT PRICE </span>
                  <br />
                  <span v-if="!isUsTenant" class="p-text-color-red"
                    >(INC TAX)</span
                  >
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ $filters.currency(item.creditAmount) }}</span>
                </v-col>
              </v-row>

              <v-divider
                v-if="isCreditReturnUpdatable"
                class="mt-3 mb-2"
              ></v-divider>

              <v-btn
                v-if="isCreditReturnUpdatable"
                class="g-full-width mt-1"
                color="secondary"
                @click="approve(item)"
                :disabled="updating || item.status === 'APPROVED'"
                :loading="updating"
                rounded
              >
                <v-icon left class="mt-1">mdi-check-circle-outline</v-icon>
                Approve Item
              </v-btn>
              <v-btn
                v-if="isCreditReturnUpdatable"
                class="g-full-width mt-1"
                color="error"
                @click="reject(item)"
                :disabled="updating || item.status === 'REJECTED'"
                :loading="updating"
                rounded
              >
                <v-icon left class="mt-1">mdi-close-circle-outline</v-icon>
                Reject Item
              </v-btn>
            </v-card>
          </v-col>
        </template>

        <template v-else>
          <v-card class="rounded-2 mt-4">
            <v-row no-gutters align="center" class="p-div-title pt-3 pb-3 px-4">
              <v-col cols="12" md="6">
                <p class="g-bold g-heading-m p-title mr-2">
                  Credit Return Reference Number
                  {{ `#${creditReturn.referenceNumber}` }}
                </p>
              </v-col>

              <v-col
                v-if="isCreditReturnApproved"
                cols="12"
                md="6"
                class="text-right"
              >
                <v-icon
                  class="ml-2"
                  color="primary"
                  :loading="fetchingPDF"
                  v-tooltip="'Export Credit Return PDF'"
                  @click="fetchPDF()"
                >
                  mdi-file-pdf-box
                </v-icon>

                <v-icon
                  class="ml-2"
                  color="primary"
                  :disabled="!creditReturn.invoiceNumber"
                  :loading="fetchingInvoicePDF"
                  v-tooltip="'Export Fees Invoice PDF'"
                  @click="fetchInvoicePDF()"
                >
                  mdi-receipt-text-check
                </v-icon>
              </v-col>
            </v-row>

            <v-row class="pa-4">
              <v-col cols="12" md="6" lg="5" class="py-0">
                <v-simple-table class="pa-0">
                  <tr>
                    <td>{{ isILR ? 'Customer' : 'Retailer' }}</td>
                    <td>{{ creditReturn.store.name }}</td>
                  </tr>

                  <tr>
                    <td>Created Date</td>
                    <td>
                      {{ $filters.formatDateTime12h(creditReturn.createdAt) }}
                    </td>
                  </tr>

                  <tr v-if="creditReturn.resolvedAt">
                    <td>Resolved Date</td>
                    <td>
                      {{ $filters.formatDateTime12h(creditReturn.resolvedAt) }}
                    </td>
                  </tr>

                  <tr>
                    <td>Credit Payment Type</td>
                    <td>
                      <i-payment-type
                        v-if="creditReturn.creditPaymentType"
                        :paymentType="creditReturn.creditPaymentType"
                        :textOnly="true"
                      />
                    </td>
                  </tr>
                </v-simple-table>
              </v-col>

              <v-spacer></v-spacer>

              <v-col cols="12" md="6" lg="4" class="py-0 text-right">
                <v-row no-gutters>
                  <v-col cols="12">
                    <p
                      class="g-bold g-border-radius-s px-2 py-3 text-center"
                      :class="[
                        { 'i-status-block-green': isCreditReturnApproved },
                        { 'i-status-block-red': isCreditReturnRejected },
                        {
                          'i-status-block-orange':
                            !isCreditReturnRejected && !isCreditReturnApproved
                        }
                      ]"
                    >
                      CREDIT RETURN STATUS:
                      {{
                        creditReturn.status === 'APPROVED' &&
                        creditReturn.hasRejectedItems
                          ? 'PARTIALLY'
                          : ''
                      }}
                      {{ creditReturn.status }}
                    </p>
                  </v-col>

                  <v-col cols="12" class="mt-1">
                    <div
                      class="g-bold g-border-radius-s i-status-block-gray pa-2"
                    >
                      <v-row no-gutters>
                        <v-col cols="9" class="text-right">
                          <span>{{ totalLabel('TOTAL PRODUCT PRICE') }}:</span>
                        </v-col>
                        <v-col cols="3" class="text-left pl-2">
                          <span>{{
                            $filters.currency(
                              supplierCostTotal(creditReturn.totalProduct)
                            )
                          }}</span>
                        </v-col>
                        <template
                          v-if="creditReturn.totalRebate.amountIncGst > 0"
                        >
                          <v-col cols="9" class="text-right">
                            <span
                              >REBATE
                              <template v-if="creditReturn.rebatePercentage">
                                /
                                {{
                                  $filters.percentage(
                                    creditReturn.rebatePercentage
                                  )
                                }}
                              </template>
                              <template v-if="!isUsTenant">(INC TAX)</template
                              ><v-icon
                                class="ml-1 mb-1"
                                color="primary"
                                small
                                v-tooltip="{
                                  ref: 'creditReturnRebateToolTipContent'
                                }"
                                >mdi-help-circle-outline</v-icon
                              >
                              <span ref="creditReturnRebateToolTipContent">
                                <span
                                  >Rebate =
                                  {{
                                    $filters.percentage(
                                      creditReturn.rebatePercentage
                                    )
                                  }}</span
                                ><br />
                                <span
                                  >Rebate % x Total Product Price (ex Tax)</span
                                > </span
                              >:
                            </span>
                          </v-col>
                          <v-col cols="3" class="text-left pl-2">
                            <span
                              >-{{
                                $filters.currency(
                                  supplierCostTotal(creditReturn.totalRebate)
                                )
                              }}</span
                            >
                          </v-col>
                        </template>

                        <template
                          v-if="
                            creditReturn.totalSupplierTradingTerms &&
                            creditReturn.totalSupplierTradingTerms
                              .amountIncGst > 0
                          "
                        >
                          <v-col cols="9" class="text-right">
                            <span
                              >TRADING TERMS
                              <template
                                v-if="
                                  creditReturn.supplierTradingTermsPercentage
                                "
                              >
                                /
                                {{
                                  $filters.percentage(
                                    creditReturn.supplierTradingTermsPercentage
                                  )
                                }}
                              </template>
                              <template v-if="!isUsTenant">(INC TAX)</template
                              ><v-icon
                                class="ml-1 mb-1"
                                color="primary"
                                small
                                v-tooltip="{
                                  ref: 'creditReturnSupplierTradingTermsToolTipContent'
                                }"
                                >mdi-help-circle-outline</v-icon
                              >
                              <span
                                ref="creditReturnSupplierTradingTermsToolTipContent"
                              >
                                <span
                                  >Trading Terms =
                                  {{
                                    $filters.percentage(
                                      creditReturn.supplierTradingTermsPercentage
                                    )
                                  }}</span
                                ><br />
                                <span
                                  >Trading Terms % x Total Product Price (ex
                                  Tax){{
                                    isUsTenant ? '' : ' + Sales Tax'
                                  }}</span
                                > </span
                              >:
                            </span>
                          </v-col>
                          <v-col cols="3" class="text-left pl-2">
                            <span
                              >-{{
                                $filters.currency(
                                  supplierCostTotal(
                                    creditReturn.totalSupplierTradingTerms
                                  )
                                )
                              }}</span
                            >
                          </v-col>
                        </template>

                        <template
                          v-if="
                            creditReturn.totalProductTradingTerms &&
                            creditReturn.totalProductTradingTerms.amountIncGst >
                              0
                          "
                        >
                          <v-col cols="9" class="text-right">
                            <span>{{ totalLabel('TRADING TERMS') }}:</span>
                          </v-col>
                          <v-col cols="3" class="text-left pl-2">
                            <span
                              >-{{
                                $filters.currency(
                                  supplierCostTotal(
                                    creditReturn.totalProductTradingTerms
                                  )
                                )
                              }}</span
                            >
                          </v-col>
                        </template>
                        <template
                          v-if="
                            creditReturn.totalPlatformFee &&
                            creditReturn.totalPlatformFee.amountIncGst > 0
                          "
                        >
                          <v-col cols="9" class="text-right">
                            <span>{{ totalLabel('PLATFORM FEE') }}:</span>
                          </v-col>
                          <v-col cols="3" class="text-left pl-2">
                            <span
                              >-{{
                                $filters.currency(
                                  supplierCostTotal(
                                    creditReturn.totalPlatformFee
                                  )
                                )
                              }}</span
                            >
                          </v-col>
                        </template>
                      </v-row>

                      <v-row
                        no-gutters
                        v-if="creditReturn.creditReturnFee.fee.amountIncGst"
                      >
                        <div class="i-total-divider"></div>
                        <v-col cols="9" class="text-right">
                          <span>{{ totalLabel('CREDIT AMOUNT') }}:</span>
                        </v-col>
                        <v-col cols="3" class="text-left pl-2">
                          <span>{{
                            $filters.currency(
                              supplierCostTotal(creditReturn.totalCredit)
                            )
                          }}</span>
                        </v-col>
                        <v-col cols="9" class="text-right">
                          <span
                            >FEE /
                            {{
                              $filters.percentage(
                                creditReturn.creditReturnFee.percentage
                              )
                            }}
                            <template v-if="!isUsTenant">(INC TAX)</template
                            >:</span
                          >
                        </v-col>
                        <v-col cols="3" class="text-left pl-2">
                          <span>{{
                            $filters.currency(
                              supplierCostTotal(
                                creditReturn.creditReturnFee.fee
                              )
                            )
                          }}</span>
                        </v-col>
                      </v-row>

                      <div class="i-total-divider"></div>

                      <v-row no-gutters>
                        <v-col cols="9" class="text-right">
                          <span>{{ totalLabel('TOTAL CREDIT AMOUNT') }}:</span>
                        </v-col>
                        <v-col cols="3" class="text-left pl-2">
                          <span>{{
                            $filters.currency(
                              supplierCostTotal(
                                creditReturn.supplierTotalIncFee
                              )
                            )
                          }}</span>
                        </v-col>
                        <v-col v-if="!isUsTenant" cols="9" class="text-right">
                          <span>TOTAL TAXES:</span>
                        </v-col>
                        <v-col
                          v-if="!isUsTenant"
                          cols="3"
                          class="text-left pl-2"
                        >
                          <span>{{
                            $filters.currency(
                              creditReturn.supplierTotalIncFee.totalTax
                            )
                          }}</span>
                        </v-col>
                        <v-col cols="9" class="text-right">
                          <span>TOTAL CREDIT QUANTITY UNITS:</span>
                        </v-col>
                        <v-col cols="3" class="text-left pl-2">
                          <span>{{ creditReturn.totalUnitQuantity }}</span>
                        </v-col>
                      </v-row>
                    </div>
                  </v-col>
                </v-row>
              </v-col>

              <v-col
                cols="12"
                v-if="creditReturn.reasonMessage"
                class="text-left"
              >
                <span class="g-bold"
                  >{{ isILR ? 'Customer' : 'Retailer' }} Credit Reason</span
                >
                <div class="g-border-radius-s i-message">
                  <span>{{ creditReturn.reasonMessage }}</span>
                </div>
              </v-col>

              <v-col
                cols="12"
                v-if="creditReturn.statusUpdateMessage"
                class="text-left mb-3"
              >
                <span class="g-bold"
                  >Message To {{ isILR ? 'Customer' : 'Retailer' }}</span
                >
                <div class="g-border-radius-s i-message">
                  <span>{{ creditReturn.statusUpdateMessage }}</span>
                </div>
              </v-col>

              <v-divider class="my-2"></v-divider>

              <v-row
                v-if="isCreditReturnUpdatable"
                no-gutters
                class="align-center mr-3"
              >
                <v-col cols="12" class="text-right">
                  <v-btn
                    class="i-btn-text-red mr-1"
                    @click="rejectAll"
                    :disabled="updating"
                    :loading="updating"
                    rounded
                    small
                    text
                  >
                    <v-icon small>mdi-close-circle-outline</v-icon>
                    Reject All
                  </v-btn>

                  <v-btn
                    color="secondary"
                    :disabled="updating"
                    :loading="updating"
                    rounded
                    small
                    @click="approveAll"
                  >
                    <v-icon small>mdi-check-circle-outline</v-icon>
                    Approve All
                  </v-btn>
                </v-col>
              </v-row>
            </v-row>

            <v-data-table
              :headers="headers"
              :items="creditReturn.items"
              :items-per-page="-1"
              item-key="id"
              hide-default-footer
              disable-sort
            >
              <template #[`header.unitPriceExTax`]="{}">
                <span
                  >UNIT PRICE
                  <span class="p-text-color-red text-no-wrap"
                    >(EX TAX)</span
                  ></span
                >
              </template>
              <template #[`header.creditAmount`]="{}">
                <span
                  >PRODUCT PRICE
                  <span v-if="!isUsTenant" class="p-text-color-red text-no-wrap"
                    >(INC TAX)</span
                  ></span
                >
              </template>
              <template #[`item.unitPriceExTax`]="{ item }">
                {{ $filters.currency(item.unitPriceExTax, '-', 2, 6) }}
              </template>
              <template #[`item.creditAmount`]="{ item }">
                {{ $filters.currency(item.creditAmount) }}
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn
                  v-if="isCreditReturnUpdatable"
                  class="ml-1"
                  color="secondary"
                  :disabled="updating || item.status === 'APPROVED'"
                  :loading="updating"
                  rounded
                  small
                  v-tooltip="'Approve Item'"
                  @click="approve(item)"
                >
                  <v-icon>mdi-check-circle-outline</v-icon>
                </v-btn>
                <v-btn
                  v-if="isCreditReturnUpdatable"
                  class="ml-1"
                  color="error"
                  :disabled="updating || item.status === 'REJECTED'"
                  :loading="updating"
                  rounded
                  small
                  v-tooltip="'Reject Item'"
                  @click="reject(item)"
                >
                  <v-icon>mdi-close-circle-outline</v-icon>
                </v-btn>
              </template>
              <template #[`item.status`]="{ item }">
                <p
                  class="g-bold g-border-radius-s px-2 py-2 my-1 text-center"
                  :class="[
                    { 'i-status-block-orange': item.status === 'OUTSTANDING' },
                    { 'i-status-block-red': item.status === 'REJECTED' },
                    { 'i-status-block-green': item.status === 'APPROVED' }
                  ]"
                  v-resize-text="{ minFontSize: '13px', maxFontSize: '15px' }"
                >
                  {{ item.status }}
                </p>
              </template>
            </v-data-table>

            <template v-if="isCreditReturnUpdatable">
              <v-divider class="my-2"></v-divider>

              <v-card-actions class="mt-n2">
                <v-row class="align-center">
                  <v-col cols="12" class="text-right">
                    <v-btn
                      class="i-btn-block-secondary"
                      @click="dialog = true"
                      :disabled="updating || hasOutstandingItems"
                      large
                      :loading="updating"
                      rounded
                    >
                      Finalise Claim
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-actions>
            </template>
          </v-card>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  fetchCreditReturn,
  fetchCreditReturnPdfUrl,
  fetchCreditReturnInvoicePdfUrl,
  finaliseCreditReturn,
  approveCreditReturnItem,
  rejectCreditReturnItem
} from '@/services/creditReturns'
import iBreadcrumbs from '@/components/Breadcrumbs'
import iOverlay from '@/components/Overlay'
import { openFile } from '@/utils/common'
import iPaymentType from '@/components/PaymentType'
import {
  buildSupplierTotalLabel,
  pickSupplierCostTotal
} from '@/utils/supplierAmountDisplay'

export default {
  components: {
    iPaymentType,
    iBreadcrumbs,
    iOverlay
  },

  props: {
    creditReturnId: String
  },

  data: () => ({
    loading: true,
    updating: false,
    fetchingPDF: false,
    fetchingInvoicePDF: false,
    dialog: false,
    answerStatus: '',
    answerUpdateMessage: '',
    supplierOrderNumber: '',
    storeName: '',
    confirmedDeliveryDate: '',
    deliveredDate: '',
    orderTotalIncTax: '',
    referenceNumber: '',
    status: '',
    requestedCreditAmountIncTax: '',
    creditReturn: {}
  }),

  computed: {
    ...mapGetters('tenant', ['isILR', 'tenantCountryRegion']),

    isUsTenant() {
      return this.tenantCountryRegion === 'US'
    },
    headers() {
      return [
        {
          title: 'CATEGORY',
          key: 'product.categoryProductHierarchy.category',
          text: 'CATEGORY',
          value: 'product.categoryProductHierarchy.category',
          width: 100
        },
        {
          title: 'SUB-CATEGORY',
          key: 'product.categoryProductHierarchy.subCategory',
          text: 'SUB-CATEGORY',
          value: 'product.categoryProductHierarchy.subCategory',
          width: 100
        },
        {
          title: 'SUPPLIER ITEM CODE',
          key: 'product.supplierItemId',
          text: 'SUPPLIER ITEM CODE',
          value: 'product.supplierItemId',
          width: 140
        },
        {
          title: 'PRODUCT DESCRIPTION',
          key: 'productDescription',
          text: 'PRODUCT DESCRIPTION',
          value: 'productDescription'
        },
        {
          title: 'UNIT GTIN',
          key: 'product.baseUnitGtin',
          text: 'UNIT GTIN',
          value: 'product.baseUnitGtin',
          width: 120
        },
        {
          title: 'UNIT PRICE (EX TAX)',
          key: 'unitPriceExTax',
          text: 'UNIT PRICE (EX TAX)',
          value: 'unitPriceExTax',
          align: 'center',
          width: 120,
          class: 'text-wrap'
        },
        {
          title: 'CREDIT QUANTITY UNITS',
          key: 'quantity',
          text: 'CREDIT QUANTITY UNITS',
          value: 'quantity',
          align: 'center',
          width: 120,
          class: 'text-wrap'
        },
        {
          title: this.isUsTenant ? 'PRODUCT PRICE' : 'PRODUCT PRICE\n(INC TAX)',
          key: 'creditAmount',
          text: this.isUsTenant ? 'PRODUCT PRICE' : 'PRODUCT PRICE\n(INC TAX)',
          value: 'creditAmount',
          align: 'center',
          width: 150,
          class: 'text-wrap'
        },
        {
          title: 'ACTIONS',
          key: 'actions',
          text: 'ACTIONS',
          value: 'actions',
          align: 'center',
          width: 140,
          updatableCreditReturnOnly: true
        },
        {
          title: 'STATUS',
          key: 'status',
          text: 'STATUS',
          value: 'status',
          align: 'center',
          width: 140
        }
      ].filter(
        (h) => !h.updatableCreditReturnOnly || this.isCreditReturnUpdatable
      )
    },

    computedBreadcrumbs() {
      let breadcrumbs = []
      if (this.isCreditReturnUpdatable) {
        breadcrumbs = [
          { text: 'Home', exact: true, to: { name: 'dashboard' } },
          {
            text: 'Credit Returns',
            exact: true,
            to: { name: 'credit-returns' }
          },
          { text: this.$route.meta.title, to: null }
        ]
      } else {
        breadcrumbs = [
          { text: 'Home', exact: true, to: { name: 'dashboard' } },
          { text: 'Reports', exact: true, to: { name: 'reports' } },
          {
            text: 'Credit Returns History',
            exact: true,
            to: { name: 'credit-returns-history' }
          },
          { text: this.$route.meta.title, to: null }
        ]
      }
      return breadcrumbs
    },

    isCreditReturnUpdatable() {
      return this.creditReturn.status === 'OUTSTANDING'
    },

    isCreditReturnApproved() {
      return this.creditReturn.status === 'APPROVED'
    },

    isCreditReturnRejected() {
      return this.creditReturn.status === 'REJECTED'
    },

    hasOutstandingItems() {
      return this.creditReturn.items.some((i) => i.status === 'OUTSTANDING')
    },

    hasRejectedItems() {
      return this.creditReturn.items.some((i) => i.status === 'REJECTED')
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    closeFinaliseDialog() {
      this.dialog = false
      this.answerStatus = ''
      this.answerUpdateMessage = ''
    },

    totalLabel(baseLabel) {
      return buildSupplierTotalLabel(this.tenantCountryRegion, baseLabel)
    },

    supplierCostTotal(cost) {
      return pickSupplierCostTotal({
        tenantCountryRegion: this.tenantCountryRegion,
        cost
      })
    },

    approveAll() {
      this.updating = true
      Promise.all(
        this.creditReturn.items.map((item) =>
          approveCreditReturnItem(this.creditReturnId, item.id)
        )
      ).then(async () => {
        await this.fetch(false)
        this.updating = false
      })
    },

    approve(item) {
      this.updating = true
      approveCreditReturnItem(this.creditReturnId, item.id).then(async () => {
        await this.fetch(false)
        this.updating = false
      })
    },

    rejectAll() {
      this.updating = true
      Promise.all(
        this.creditReturn.items.map((item) =>
          rejectCreditReturnItem(this.creditReturnId, item.id)
        )
      ).then(async () => {
        await this.fetch(false)
        this.updating = false
      })
    },

    reject(item) {
      this.updating = true

      rejectCreditReturnItem(this.creditReturnId, item.id).then(async () => {
        await this.fetch(false)
        this.updating = false
      })
    },

    finalise() {
      this.loading = true

      const payload = {
        status: this.answerStatus,
        statusUpdateMessage: this.answerUpdateMessage
      }

      finaliseCreditReturn(this.creditReturnId, payload)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Credit Return status successfully updated'
          })
          this.closeFinaliseDialog()
          this.fetch()
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Credit Return status could not be updated...'
          })
          this.loading = false
          this.closeFinaliseDialog()
        })
    },

    fetch(loading = true) {
      this.loading = loading

      return fetchCreditReturn(this.creditReturnId)
        .then((res) => {
          this.creditReturn = res
          this.creditReturn.items = this.mapCreditReturnItems(
            this.creditReturn.items
          )
        })
        .catch(() => {
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.loading = false
        })
    },

    fetchPDF() {
      this.fetchingPDF = true
      fetchCreditReturnPdfUrl(this.creditReturn.id)
        .then((response) => {
          openFile(response.url)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Credit Return ${this.creditReturn.id} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.fetchingPDF = false
        })
    },

    fetchInvoicePDF() {
      this.fetchingInvoicePDF = true
      fetchCreditReturnInvoicePdfUrl(this.creditReturn.id)
        .then((response) => {
          openFile(response.url)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Credit Return ${this.creditReturn.id} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.fetchingInvoicePDF = false
        })
    },

    mapCreditReturnItems(items) {
      return items.map((item) => {
        return {
          ...item,
          creditAmount: this.supplierCostTotal(item.totalPrice),
          productDescription: item.product.tradeItemDescription.replace(
            /\s*X\s*\d+$/,
            ''
          )
        }
      })
    }
  },

  mounted() {
    this.fetch()
  }
}
</script>

<style lang="scss" scoped>
#credit-return-details {
  table {
    border-collapse: collapse;
    tr > td {
      padding: 2px 0px;
    }
    tr > td:nth-of-type(1) {
      font-weight: bold;
    }
  }
  .p-div-title {
    background-color: $primary-color-light;
    color: $primary-color;
  }
  .p-text-color-red {
    color: red !important;
  }
  .p-title {
    display: inline-block;
    letter-spacing: 0.75px;
  }
}
</style>
